import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type VocabKind =
  | 'mood'
  | 'tone_modifiers'
  | 'genre_tone'
  | 'narrative_style';

export interface VocabEntry {
  id: string;
  kind: VocabKind;
  label: string;
  source: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useVocab(kind: VocabKind) {
  const { getToken } = useAuth();
  return useQuery<VocabEntry[]>({
    queryKey: ['vocab', kind],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/vocab/${kind}`)
        .json<ApiResponse<VocabEntry[]>>();
      return response.data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateVocab(kind: VocabKind) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/vocab/${kind}`, { json: { label } })
        .json<ApiResponse<VocabEntry>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['vocab', kind] }),
  });
}
